/**
 * Home curated-card image resolution (029 US1 / T011, research D3).
 *
 * Extracted from `HomeCards.tsx` so the component stays under the 400-line
 * architecture ceiling (`file-size-400.architecture.test.ts`) while owning a
 * single, testable concern: turning a `CuratedCard` into the `<img src>` the
 * strip renders.
 *
 * Resolution order (authored-first, D3 / D-094 posture — no frontend image
 * shadow over authored content):
 *
 *   1. `card.imageUrl` — the authored snapshot image (a same-origin
 *      `/media/<uuid>.png` path or an absolute https asset URL). This is the
 *      production path: every seeded `home_card` snapshot carries an
 *      `imageUrl`, so this branch fires for all live cards.
 *   2. The slug-keyed BUNDLED fallback — the original per-slug PNG for the
 *      five known live curated slugs. This is the ABSENCE fallback only: it
 *      fires when a card's snapshot omits `imageUrl` (zero-regression for a
 *      card that predates authored images), NOT as an override of an authored
 *      image. The map is deliberately scoped to the five known slugs; it is
 *      not a general slug->image registry (that was the pre-029 source of
 *      truth this feature removes).
 *   3. A single generic bundled default — the last-resort image for a
 *      never-seen card with neither an authored image nor a known slug. In
 *      production this never fires (authored cards carry `imageUrl`); it
 *      exists so an unknown-slug card still renders a non-broken tile rather
 *      than being silently dropped (FR-002 / SC-006). `usedGenericDefault`
 *      surfaces this case so the caller can emit a non-fatal dev note.
 */

import eduhouseKoulutusImage from '../../assets/eduhouse-koulutus.png';
import hyvinvointiTyokykyImage from '../../assets/hyvinvointi-tyokyky.png';
import lakikaveriCardImage from '../../assets/lakikaveri-card.png';
import tyonhakuSoveltuvuusarvioImage from '../../assets/tyonhaku-soveltuvuusarvio.png';
import vakuutusVapaalleImage from '../../assets/vakuutus-vapaalle.png';
// Neutral brand placeholder used only as the generic last-resort default for a
// card that carries neither an authored image nor a known slug. Production
// never reaches this branch (authored cards always carry `imageUrl`).
import ytkLogoImage from '../../assets/ytk-logo.png';
import type { CuratedCard } from '../../content/ContentReaderPort.js';

/**
 * The five known live curated slugs and their original bundled PNG. Used ONLY
 * as the absence fallback (resolution step 2) for a card whose snapshot omits
 * `imageUrl`. Keyed on `CuratedCard.slug`: the adapter projects
 * `slug = COALESCE(snapshot->>'linkSlug', content_items.slug)`, and for these
 * five seeded cards the snapshot `linkSlug` IS the article slug, so `card.slug`
 * equals the article slug the keys below name. (The authored jasenedut card's
 * `card.slug` is `'jasenedut'`, deliberately absent here so its authored
 * `imageUrl` is used, never a bundled fallback.)
 */
const BUNDLED_IMAGE_BY_SLUG: Readonly<Record<string, string>> = {
  'tyosuhteen-paattaminen': tyonhakuSoveltuvuusarvioImage,
  'puhetta-palkasta': hyvinvointiTyokykyImage,
  'lakikaveri-info': lakikaveriCardImage,
  'eduhousen-verkkokoulutus': eduhouseKoulutusImage,
  'vapaa-ajan-tapaturmavakuutus': vakuutusVapaalleImage,
};

/** The generic last-resort card image (resolution step 3). */
export const GENERIC_HOME_CARD_IMAGE = ytkLogoImage;

/** Resolved image for a curated card plus whether the generic default fired. */
export interface ResolvedHomeCardImage {
  readonly src: string;
  /**
   * `true` when neither an authored `imageUrl` nor a known-slug bundled image
   * resolved and the generic default was used. The caller emits a non-fatal
   * dev-mode note in this case so a backend-seeded card missing both signals
   * is observable in development without dropping the card.
   */
  readonly usedGenericDefault: boolean;
}

/**
 * Resolve the `<img src>` for a curated card, authored-first. Total by
 * construction — always returns a renderable `src` (never throws, never
 * returns null), so a card is never silently dropped for lacking an image.
 */
export function resolveHomeCardImage(card: CuratedCard): ResolvedHomeCardImage {
  if (card.imageUrl !== null && card.imageUrl.length > 0) {
    return { src: card.imageUrl, usedGenericDefault: false };
  }
  const bundled = BUNDLED_IMAGE_BY_SLUG[card.slug];
  if (bundled !== undefined) {
    return { src: bundled, usedGenericDefault: false };
  }
  return { src: GENERIC_HOME_CARD_IMAGE, usedGenericDefault: true };
}

/**
 * The bundled photo to fall back to when a card's resolved image FAILS TO LOAD
 * at runtime (e.g. an authored `/media/<uuid>.png` that 404s). This is the
 * load-FAILURE fallback — distinct from `resolveHomeCardImage`'s authored-image
 * ABSENCE fallback (resolution step 2): absence picks the bundled photo before
 * any render; this recovers AFTER the browser reports an `<img>` error. Returns
 * the slug-keyed bundled PNG for the five known live slugs, else the generic
 * default, so a load failure degrades to a real photo rather than a bare
 * gradient. Total/pure — always returns a renderable `src`.
 */
export function bundledFallbackFor(slug: string): string {
  return BUNDLED_IMAGE_BY_SLUG[slug] ?? GENERIC_HOME_CARD_IMAGE;
}
